import { Box } from '@mui/material'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { UseFormReset } from 'react-hook-form'
import { saveStatus, SaveStatus } from '../../types/save-status.types'
import { DeleteStatusRow } from './DeleteStatusRow'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFetchInstallation } from 'src/Libs/flow/installation/hooks'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

export type DeleteRequest = {
  uid: string
  message: string
  request: () => Promise<unknown>
}

export interface InstallationDeleteDialogProps {
  installationId: number
  requests: DeleteRequest[]
  reset: UseFormReset<InstallationFormModel>
  shouldSync: boolean
  onClose?: () => void
}

export const InstallationDeleteDialog: React.FC<InstallationDeleteDialogProps> = ({ installationId, requests, reset, shouldSync, onClose }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()

  /**
   * Counter for the number of operations that have not finished yet.
   * Goes down by one each time an operation finishes.
   */
  const [operationsPending, setOperationsPending] = useState(() => requests.length)
  const countOperationFinished = (): void => setOperationsPending(operations => operations - 1)

  const operationsFinished = operationsPending <= 0

  const [isSyncPending, setIsSyncPending] = useState(shouldSync)

  const queryClient = useQueryClient()

  const hasFired = useRef(false)
  useEffect(() => {
    if (operationsFinished && !hasFired.current) {
      hasFired.current = true
      queryClient.invalidateQueries({ queryKey: ['installation', installationId] })
    }
  }, [queryClient, operationsFinished, installationId])

  const { installation: newInstallationValues } = useFetchInstallation(installationId, operationsFinished)

  const isInstallationPending = !!useIsFetching({ queryKey: ['installation', installationId] })

  const canClose = operationsFinished && !isSyncPending && !isInstallationPending && !!newInstallationValues

  const handleCloseDialog = (): void => {
    if (!canClose) return

    reset(newInstallationValues)
    onClose?.()
  }

  return (
    <Dialog
      title={t('installation.delete.title')}
      onClose={handleCloseDialog}
      maxWidth={'md'}
      actions={{
        primary: {
          name: t('common.ok'),
          onClick: handleCloseDialog,
          disabled: !canClose,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {requests.map(({ uid, message, request }) => (
          <DeleteRow key={uid} name={message} request={request} onSettled={countOperationFinished} />
        ))}
        {shouldSync && (
          <DeleteRow
            name={t('installation.save.synchronization')}
            request={() => syncInstallation(installationId, authFetch)}
            onSettled={() => setIsSyncPending(false)}
            loadingLabel={t('installation.sync.status.inProgress')}
            completeLabel={t('installation.sync.status.done')}
            disabled={!operationsFinished}
          />
        )}
      </Box>
    </Dialog>
  )
}

type DeleteRowProps<T> = {
  name: string
  request: () => Promise<T>
  onSettled: () => void
  disabled?: boolean
  /** Custom label to display in the loading status chip instead of the default one. */
  loadingLabel?: string
  /** Custom label to display in the complete status chip instead of the default one. */
  completeLabel?: string
}

/* eslint-disable-next-line  */
const DeleteRow: React.FC<DeleteRowProps<any>> = ({ name, request, onSettled, disabled, loadingLabel, completeLabel }) => {
  const [status, setStatus] = useState<SaveStatus>(saveStatus.idle)

  const { mutate, error, isIdle } = usePortalMutation({
    mutationFn: async () => new ApiResult(await request()),
    onMutate: () => {
      setStatus(saveStatus.loading)
    },
    onError: () => {
      setStatus(saveStatus.error)
    },
    onSuccess: _data => {
      setStatus(saveStatus.complete)
    },
    onSettled,
  })

  // Workaround to send the request once, even during development.
  const hasFired = useRef(false)
  useEffect(() => {
    if (!disabled && isIdle && !hasFired.current) {
      hasFired.current = true
      mutate()
    }
  }, [disabled, isIdle, mutate])

  return (
    <DeleteStatusRow
      name={name}
      status={status}
      errorMessage={error?.message}
      disabled={disabled ?? false}
      loadingLabel={loadingLabel}
      completeLabel={completeLabel}
    />
  )
}
